import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Dialog as MuiDialog,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  Button as MuiButton,
  Box as MuiBox,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

interface InactivityWarningDialogProps {
  open: boolean;
  minutesUntilLogout: number;
  onStayLoggedIn: () => void;
}

/**
 * Dialog component that warns users about impending automatic logout
 * due to inactivity and displays the supplied remaining duration.
 */
const InactivityWarningDialog: React.FC<InactivityWarningDialogProps> = ({
  open,
  minutesUntilLogout,
  onStayLoggedIn,
}) => {
  const { t } = useTranslation();

  return (
    <MuiDialog open={open} onClose={onStayLoggedIn}>
      <SDialogContent sx={{ minWidth: '400px' }}>
        <SIconWrapper>
          <WarningIcon color="warning" />
        </SIconWrapper>
        <STitle>{t('Inactivity warning')}</STitle>
        <SSubTitle>
          {t(
            'You have been inactive for a while. You will be logged out in {{count}} minute',
            { count: minutesUntilLogout }
          )}
        </SSubTitle>
      </SDialogContent>
      <SDialogActions>
        <MuiBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
        >
          <MuiButton
            type="button"
            variant="contained"
            onClick={onStayLoggedIn}
            color="primary"
          >
            {t('Stay logged in')}
          </MuiButton>
        </MuiBox>
      </SDialogActions>
    </MuiDialog>
  );
};

const SDialogContent = styled(MuiDialogContent)`
  padding: 0 50px;
`;

const SIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 40px 0 30px;
  font-size: 64px;
`;

const STitle = styled.div`
  font-weight: 600;
  font-size: 20px;
  line-height: 27px;
  text-align: center;
  color: ${({ theme }) => theme.palette.colors.black100};
`;

const SSubTitle = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  text-align: center;
  margin-top: 10px;
  color: ${({ theme }) => theme.palette.colors.black100};
`;

const SDialogActions = styled(MuiDialogActions)`
  margin: 20px 0;
  padding: 0 50px;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

export default InactivityWarningDialog;
